import React, { useEffect, useMemo, useState } from 'react';

import { ComboBox, Item, Pill } from '@components';

import { SimpleSite } from '../types';

import Link from './Link';

interface AccountMenuListProps {
  pageAccountSlug: string;
  selectedAccountSlug: string;
  accountsList: SimpleSite[];
  setSelectedAccountSlug: (account: string) => void;
  currentActiveNav: string;
}

const navigateToAccountDashboard = (accountSlug) => {
  return window.location.assign(`/${accountSlug}/dashboard/`);
};

const mapPillColourToEnvironment = {
  production: 'green',
  staging: 'purple',
  qa: 'yellow',
  dev: 'gray',
  uat: 'blue',
  lower_envs: 'red',
};

const AccountMenuList = ({
  pageAccountSlug,
  selectedAccountSlug,
  accountsList,
  setSelectedAccountSlug,
  currentActiveNav,
}: AccountMenuListProps): JSX.Element => {
  const [accountSearch, setAccountSearch] = useState<string>('');

  useEffect(() => {
    // Keep the visible input in sync with the selected account name
    const selected = accountsList?.find((a) => a.slug === selectedAccountSlug);
    setAccountSearch(selected?.name || '');
  }, [selectedAccountSlug, accountsList]);

  const isAccountMenuShown =
    !!selectedAccountSlug &&
    (accountsList?.length === 1 || selectedAccountSlug === pageAccountSlug);

  const handleChangeAccount = (slug) => {
    if (!slug || slug === selectedAccountSlug) return;

    setSelectedAccountSlug(slug);
    navigateToAccountDashboard(slug);

    // We use selectedAccount to get the name of the account to display in the input
    const selectedAccount = accountsList?.find((a) => a.slug === slug);
    if (selectedAccount) {
      setAccountSearch(selectedAccount.name);
    }
  };

  // Filtered accounts allow searching by account name or slug
  const filteredAccounts = useMemo(() => {
    if (!accountSearch?.trim()) return accountsList;

    // If the search matches the currently selected account, we want to show all accounts
    // so the user can see other options when opening the menu
    const selectedAccount = accountsList?.find(
      (a) => a.slug === selectedAccountSlug,
    );
    if (selectedAccount && accountSearch === selectedAccount.name) {
      return accountsList;
    }

    const searchLower = accountSearch.toLowerCase();
    return accountsList.filter(
      (account) =>
        account.name.toLowerCase().includes(searchLower) ||
        account.slug.toLowerCase().includes(searchLower),
    );
  }, [accountsList, accountSearch, selectedAccountSlug]);

  let accountsSelector;

  if (accountsList && accountsList.length === 1) {
    accountsSelector = accountsList.map((account: SimpleSite) => (
      <h4 key={account.slug} className="px-4 py-2 text-base font-semibold">
        {account.name}
      </h4>
    ));
  }

  if (accountsList && accountsList.length > 1) {
    accountsSelector = (
      <ComboBox
        showBorder={false}
        placeholder="Select a site"
        items={filteredAccounts}
        inputValue={accountSearch}
        onInputChange={setAccountSearch}
        onSelectionChange={handleChangeAccount}
        selectedKey={selectedAccountSlug}
        aria-labelledby={'Sites List Option'}
      >
        {(item: SimpleSite) => (
          <Item key={item.slug} textValue={item.name}>
            <div className="flex w-full items-center justify-between">
              <div>{item.name}</div>
              <span className="mx-2">
                <Pill
                  text={item.environment}
                  colour={mapPillColourToEnvironment[item.environment]}
                />
              </span>
            </div>
          </Item>
        )}
      </ComboBox>
    );
  }

  const accountMenuItems = [
    {
      name: 'Site Dashboard',
      path: `/${selectedAccountSlug}/dashboard/`,
      isShown: isAccountMenuShown,
      isDisabled: !selectedAccountSlug,
      isActive: currentActiveNav === 'dashboard',
    },
    {
      name: 'Test Dashboard',
      path: `/${selectedAccountSlug}/testing/`,
      isShown: isAccountMenuShown,
      isDisabled: !selectedAccountSlug,
      isActive: currentActiveNav === 'test-dashboard',
    },
    {
      name: 'Datasets',
      path: `/${selectedAccountSlug}/datasets/list/`,
      isShown: isAccountMenuShown,
      isDisabled: !selectedAccountSlug,
      isActive: currentActiveNav === 'datasets',
    },
    {
      name: 'Settings',
      path: `/${selectedAccountSlug}/settings/`,
      isShown: isAccountMenuShown,
      isDisabled: !selectedAccountSlug,
      isActive: currentActiveNav === 'settings',
    },
    {
      name: 'Publish History',
      path: `/${selectedAccountSlug}/publish/history`,
      isShown: isAccountMenuShown,
      isDisabled: !selectedAccountSlug,
      isActive: currentActiveNav === 'publish_history',
    },
  ];

  const menuItems = accountMenuItems
    .filter((item) => item.isShown)
    .map((item, index) => {
      return (
        <Link
          key={`${item}-${index}`}
          href={selectedAccountSlug !== '' ? item.path : undefined}
          disabled={item.isDisabled}
          label={item.name}
          isActive={item.isActive}
        />
      );
    });

  return (
    <div>
      <div className="flex px-2 py-1">{accountsSelector}</div>
      <ul>{menuItems}</ul>
    </div>
  );
};

export default AccountMenuList;
